import { apiFetch, apiUrl } from "@/core/lib/api";
import { ApiError } from "@/core/lib/incident-api";

export { ApiError };

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await apiFetch(apiUrl(path), {
      credentials: "include",
      headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
      ...init,
    });
  } catch (err) {
    if (err instanceof TypeError && /fetch/i.test(err.message)) {
      throw new ApiError("Can't reach the server.", 0);
    }
    throw err;
  }
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const message =
      (data && typeof data === "object" && "error" in data
        ? String((data as { error: unknown }).error)
        : null) ?? `Request failed (${res.status})`;
    throw new ApiError(message, res.status);
  }
  return data as T;
}

export type ActorRole = "employee" | "hr";

export type OnboardingStage = "pre_start" | "week_1" | "month_1" | "anytime";

export type OnboardingTemplateTask = {
  id: string;
  label: string;
  description: string | null;
  sortOrder: number;
  actorRole: ActorRole;
  linkHref: string | null;
  stage: OnboardingStage;
  dueOffsetDays: number | null;
  createdAt: string;
  updatedAt: string;
};

export type OnboardingTask = {
  id: string;
  employeeId: string;
  label: string;
  description: string | null;
  sortOrder: number;
  actorRole: ActorRole;
  linkHref: string | null;
  stage: OnboardingStage;
  dueDate: string | null;
  status: "pending" | "done";
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type TemplateTaskInput = {
  label: string;
  description?: string;
  actorRole?: ActorRole;
  linkHref?: string;
  stage?: OnboardingStage;
  dueOffsetDays?: number | null;
  sortOrder?: number;
};

export type EmployeeTaskInput = {
  label: string;
  description?: string;
  actorRole?: ActorRole;
  linkHref?: string;
  stage?: OnboardingStage;
  dueDate?: string | null;
};

export type OnboardingStageCounts = { total: number; done: number };

export type OnboardingEmployeeSummary = {
  employeeId: string;
  name: string;
  firstName: string;
  lastName: string;
  jobTitle: string;
  department: string;
  profilePhotoUrl: string | null;
  startDate: string | null;
  hireDate: string;
  total: number;
  done: number;
  overdue: number;
  hrPending: number;
  nextDue: string | null;
  stages: Record<OnboardingStage, OnboardingStageCounts>;
};

export type OnboardingNotStartedEmployee = {
  employeeId: string;
  name: string;
  firstName: string;
  lastName: string;
  jobTitle: string;
  department: string;
  profilePhotoUrl: string | null;
  startDate: string | null;
  hireDate: string;
};

export type OnboardingHrTask = OnboardingTask & { employeeName: string };

export type OnboardingOverview = {
  today: string;
  employees: OnboardingEmployeeSummary[];
  notStarted: { total: number; employees: OnboardingNotStartedEmployee[] };
  hrTasks: { total: number; tasks: OnboardingHrTask[] };
};

export type BulkOnboardingResult = { employees: number; tasksCreated: number };

/* ── Workspace overview ── */
export function fetchOnboardingOverview(): Promise<OnboardingOverview> {
  return request<OnboardingOverview>("/api/onboarding/overview");
}
export function fetchOnboardingCounts(): Promise<{ overdue: number }> {
  return request<{ overdue: number }>("/api/onboarding/counts");
}
export function seedAllOnboarding(): Promise<BulkOnboardingResult> {
  return request("/api/onboarding/seed-all", { method: "POST" });
}
export function syncAllOnboarding(): Promise<BulkOnboardingResult> {
  return request("/api/onboarding/sync-all", { method: "POST" });
}

/* ── Template ── */
export function listTemplateTasks(): Promise<OnboardingTemplateTask[]> {
  return request<OnboardingTemplateTask[]>("/api/onboarding/template");
}
export function createTemplateTask(body: TemplateTaskInput): Promise<OnboardingTemplateTask> {
  return request("/api/onboarding/template", {
    method: "POST",
    body: JSON.stringify(body),
  });
}
export function updateTemplateTask(
  id: string,
  body: TemplateTaskInput,
): Promise<OnboardingTemplateTask> {
  return request(`/api/onboarding/template/${id}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
}
export function deleteTemplateTask(id: string): Promise<{ ok: boolean }> {
  return request(`/api/onboarding/template/${id}`, { method: "DELETE" });
}
export function reorderTemplateTasks(ids: string[]): Promise<{ ok: boolean }> {
  return request("/api/onboarding/template/reorder", {
    method: "POST",
    body: JSON.stringify({ ids }),
  });
}

/* ── Per-employee ── */
export function listEmployeeTasks(employeeId: string): Promise<OnboardingTask[]> {
  return request<OnboardingTask[]>(`/api/onboarding/employee/${employeeId}`);
}
export function seedEmployeeTasks(employeeId: string): Promise<OnboardingTask[]> {
  return request(`/api/onboarding/employee/${employeeId}/seed`, {
    method: "POST",
  });
}
export function syncEmployeeTasks(employeeId: string): Promise<OnboardingTask[]> {
  return request(`/api/onboarding/employee/${employeeId}/sync`, {
    method: "POST",
  });
}
export function addEmployeeTask(
  employeeId: string,
  body: EmployeeTaskInput,
): Promise<OnboardingTask> {
  return request(`/api/onboarding/employee/${employeeId}`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}
export function updateEmployeeTask(
  employeeId: string,
  taskId: string,
  body: EmployeeTaskInput,
): Promise<OnboardingTask> {
  return request(`/api/onboarding/employee/${employeeId}/${taskId}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
}
export function reorderEmployeeTasks(employeeId: string, ids: string[]): Promise<{ ok: boolean }> {
  return request(`/api/onboarding/employee/${employeeId}/reorder`, {
    method: "POST",
    body: JSON.stringify({ ids }),
  });
}
export function toggleEmployeeTask(
  employeeId: string,
  taskId: string,
  done: boolean,
): Promise<OnboardingTask> {
  return request(`/api/onboarding/employee/${employeeId}/${taskId}/toggle`, {
    method: "POST",
    body: JSON.stringify({ done }),
  });
}
export function deleteEmployeeTask(employeeId: string, taskId: string): Promise<{ ok: boolean }> {
  return request(`/api/onboarding/employee/${employeeId}/${taskId}`, {
    method: "DELETE",
  });
}
